import React, { useState, useEffect, useMemo } from 'react'
import { TopScrollSync } from '@/components/table/TopScrollSync'
import { useDragScroll } from '@/hooks/useDragScroll'
import { DateInput } from '@/components/forms/DateInput'
import { Typeahead } from '@/components/forms/Typeahead'
import { formatDate } from '@/lib/dateFormat'
import { Button } from '@/components/ui/Button'
import type { ProductionOrder, WorkUnit } from '@/services/productionOrders'
import {
  validateMilestoneOrder,
  reorderItems,
  getStatusBadgeInfo,
  formatDimPure,
} from './productionOrderUtils'
import { ProductionOrderRowMenu } from './ProductionOrderRowMenu'

export interface ProductionOrderGridProps {
  orders: ProductionOrder[]
  workUnits: WorkUnit[]
  canWrite: boolean
  monthLocked: boolean
  onUpdateOrder: (id: string, updates: Partial<ProductionOrder>) => void
  onReorder: (newOrders: ProductionOrder[]) => void
  onSavePlan: () => Promise<void>
  isSaving?: boolean
  autoSortOnSave: boolean
  onToggleAutoSort: (enabled: boolean) => void
  onUndoLastSave?: () => void
  canUndo?: boolean
  onAckChange: (id: string) => Promise<void>
  onViewHistory: (order: ProductionOrder) => void
  onReturnToDraft: (order: ProductionOrder) => void
  onPushToDemand: (order: ProductionOrder) => void
  onOpenProductionDemand?: (order: ProductionOrder) => void
  onOpenExecution?: (order: ProductionOrder) => void
  flashingIds?: string[]
}

function ProductThumbnail({
  productId,
  hasImage,
  alt,
  className,
}: {
  productId: string | null | undefined
  hasImage: boolean | undefined
  alt: string
  className: string
}): React.ReactElement {
  const [imageFailed, setImageFailed] = useState(false)
  if (!productId || !hasImage || imageFailed) {
    return <span className="text-xs text-slate-300" aria-label="Chưa có ảnh sản phẩm">📦</span>
  }
  return (
    <img
      src={`/api/products/${encodeURIComponent(productId)}/image`}
      alt={alt}
      className={className}
      onError={() => setImageFailed(true)}
    />
  )
}

export function ProductionOrderGrid({
  orders,
  workUnits,
  canWrite,
  monthLocked,
  onUpdateOrder,
  onReorder,
  onSavePlan,
  isSaving = false,
  autoSortOnSave,
  onToggleAutoSort,
  onUndoLastSave,
  canUndo = false,
  onAckChange,
  onViewHistory,
  onReturnToDraft,
  onPushToDemand,
  onOpenProductionDemand,
  onOpenExecution,
  flashingIds = [],
}: ProductionOrderGridProps): React.ReactElement {
  // Sticky toggle
  const [isSticky, setIsSticky] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1400
    }
    return true
  })

  // Selected row for keyboard shortcuts
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Drag scroll hook for the table container
  const scrollContainerRef = useDragScroll<HTMLDivElement>()

  // Drag and drop state
  const [draggedId, setDraggedId] = useState<string | null>(null)
  const [dragOverId, setDragOverId] = useState<string | null>(null)
  const [dragOverPos, setDragOverPos] = useState<'top' | 'bottom' | null>(null)

  // Menu anchor state
  const [menuState, setMenuState] = useState<{
    order: ProductionOrder | null
    anchorRect: DOMRect | null
  }>({ order: null, anchorRect: null })

  // Hover image preview
  const [hoverCard, setHoverCard] = useState<{
    visible: boolean
    order: ProductionOrder | null
    x: number
    y: number
  }>({ visible: false, order: null, x: 0, y: 0 })

  // Filter work units by category
  const woodUnits = useMemo(
    () => workUnits.filter((u) => u.kind === 'wood' && u.status === 'active'),
    [workUnits],
  )
  const paintUnits = useMemo(
    () => workUnits.filter((u) => u.kind === 'paint' && u.status === 'active'),
    [workUnits],
  )
  const subcontractUnits = useMemo(
    () => workUnits.filter((u) => u.kind === 'subcontract' && u.status === 'active'),
    [workUnits],
  )

  // Alt+ArrowUp / Alt+ArrowDown keyboard shortcuts for reordering
  useEffect(() => {
    if (!canWrite || monthLocked) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return
      if (!selectedId) return

      const idx = orders.findIndex((o) => o.id === selectedId)
      if (idx === -1) return

      if (e.key === 'ArrowUp' && idx > 0) {
        e.preventDefault()
        const reordered = reorderItems(orders, idx, idx - 1)
        onReorder(reordered)
      } else if (e.key === 'ArrowDown' && idx < orders.length - 1) {
        e.preventDefault()
        const reordered = reorderItems(orders, idx, idx + 1)
        onReorder(reordered)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [canWrite, monthLocked, selectedId, orders, onReorder])

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, id: string) => {
    if (!canWrite || monthLocked) return
    e.dataTransfer.setData('text/plain', id)
    e.dataTransfer.effectAllowed = 'move'
    setDraggedId(id)
    setSelectedId(id)
  }

  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    if (!canWrite || monthLocked || !draggedId || draggedId === targetId) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'

    const rect = e.currentTarget.getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    const pos = e.clientY < midY ? 'top' : 'bottom'
    setDragOverId(targetId)
    setDragOverPos(pos)
  }

  const handleDragLeave = () => {
    setDragOverId(null)
    setDragOverPos(null)
  }

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault()
    if (!canWrite || monthLocked || !draggedId || draggedId === targetId) {
      setDragOverId(null)
      setDragOverPos(null)
      setDraggedId(null)
      return
    }

    const fromIdx = orders.findIndex((o) => o.id === draggedId)
    let toIdx = orders.findIndex((o) => o.id === targetId)

    if (fromIdx !== -1 && toIdx !== -1) {
      if (dragOverPos === 'bottom' && toIdx > fromIdx) {
        // already after
      } else if (dragOverPos === 'bottom' && toIdx < fromIdx) {
        toIdx += 1
      } else if (dragOverPos === 'top' && toIdx > fromIdx) {
        toIdx -= 1
      }
      const reordered = reorderItems(orders, fromIdx, toIdx)
      onReorder(reordered)
    }

    setDragOverId(null)
    setDragOverPos(null)
    setDraggedId(null)
  }

  const handleDragEnd = () => {
    setDragOverId(null)
    setDragOverPos(null)
    setDraggedId(null)
  }

  // Row selection handler
  const handleRowClick = (id: string) => {
    setSelectedId(id)
  }

  // Dimension unit toggle for an order
  const handleToggleDimUnit = (order: ProductionOrder) => {
    const nextUnit = (order.dim_unit || 'inch') === 'inch' ? 'mm' : 'inch'
    onUpdateOrder(order.id, { dim_unit: nextUnit })
  }

  const isEditable = canWrite && !monthLocked

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-2">
      {/* Action Bar above Grid */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3 rounded-lg border border-slate-200 shadow-sm text-sm">
        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-700 select-none">
            <input
              type="checkbox"
              checked={autoSortOnSave}
              onChange={(e) => onToggleAutoSort(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              disabled={!isEditable}
            />
            <span>Khi lưu: tự động sắp xếp theo ngày Hoàn thành (nhà máy) tăng dần</span>
          </label>

          <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isSticky}
              onChange={(e) => setIsSticky(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-300 text-slate-600"
            />
            <span>Cố định cột mã hàng</span>
          </label>
        </div>

        <div className="flex items-center gap-2">
          {canUndo && onUndoLastSave && isEditable && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onUndoLastSave}
              title="Khôi phục thứ tự các dòng trước lần lưu gần nhất"
            >
              ↩ Hoàn tác lần lưu gần nhất
            </Button>
          )}

          {canWrite && !monthLocked && (
            <Button
              variant="primary"
              size="sm"
              onClick={onSavePlan}
              disabled={isSaving}
              className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold"
            >
              {isSaving ? 'Đang lưu...' : '💾 Lưu kế hoạch'}
            </Button>
          )}
        </div>
      </div>

      {/* Top Sync Scrollbar */}
      <TopScrollSync targetRef={scrollContainerRef} />

      {/* Table Container */}
      <div
        ref={scrollContainerRef}
        data-testid="production-order-grid-container"
        className="relative min-h-0 min-w-0 flex-1 overflow-auto rounded-lg border border-slate-300 bg-white shadow-sm"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <table className={`excel-table w-full table-fixed border-collapse text-sm ${isSticky ? 'has-sticky' : ''}`}>
          <colgroup>
            {/* 1..5 Thông tin sản phẩm (Sticky) */}
            <col className="w-12" />
            <col className="w-28" />
            <col className="w-44" />
            <col className="w-16" />
            <col className="w-36" />

            {/* 6..15 Bán hàng (Chỉ đọc) */}
            <col className="w-28" />
            <col className="w-56" />
            <col className="w-14" />
            <col className="w-14" />
            <col className="w-14" />
            <col className="w-20" />
            <col className="w-16" />
            <col className="w-20" />
            <col className="w-28" />
            <col className="w-36" />

            {/* 16..26 Kế hoạch sản xuất */}
            <col className="w-24" />
            <col className="w-36" />
            <col className="w-36" />
            <col className="w-36" />
            <col className="w-28" />
            <col className="w-28" />
            <col className="w-28" />
            <col className="w-36" />
            <col className="w-28" />
            <col className="w-24" />
            <col className="w-28" />

            {/* 27..28 Thao tác */}
            <col className="w-44" />
            <col className="w-12" />
          </colgroup>
          <thead className="sticky top-0 z-20 bg-slate-100 shadow-sm">
            {/* Super Header Row */}
            <tr className="super-header-row bg-slate-100 text-xs font-bold uppercase tracking-wider text-slate-700 border-b border-slate-300">
              <th
                colSpan={5}
                className={`border-r-2 border-slate-300 py-1.5 text-center bg-slate-200 ${
                  isSticky ? 'sticky left-0 z-40 bg-slate-200' : ''
                }`}
              >
                Thông tin sản phẩm
              </th>
              <th colSpan={10} className="border-r-2 border-slate-300 py-1.5 text-center bg-slate-100 text-slate-600">
                Bán hàng (Chỉ đọc)
              </th>
              <th colSpan={11} className="border-r-2 border-slate-300 py-1.5 text-center bg-sky-100 text-sky-900 font-extrabold">
                Kế hoạch sản xuất
              </th>
              <th colSpan={2} className="py-1.5 text-center bg-slate-200 text-slate-700">
                Thao tác
              </th>
            </tr>

            {/* Column Header Row */}
            <tr className="column-header-row bg-slate-50 text-sm font-semibold text-slate-700 border-b border-slate-300 whitespace-nowrap">
              {/* Sticky Columns */}
              <th className={`border-r border-slate-200 px-2 py-2 text-center w-12 ${isSticky ? 'sticky left-0 z-40 bg-slate-100' : ''}`}>
                STT
              </th>
              <th className={`border-r border-slate-200 px-2.5 py-2 text-left w-28 ${isSticky ? 'sticky left-12 z-40 bg-slate-100' : ''}`}>
                Khách hàng
              </th>
              <th className={`border-r border-slate-200 px-2.5 py-2 text-left w-44 ${isSticky ? 'sticky left-40 z-40 bg-slate-100' : ''}`}>
                Người mua
              </th>
              <th className={`border-r border-slate-200 px-2 py-2 text-center w-16 ${isSticky ? 'sticky left-[336px] z-40 bg-slate-100' : ''}`}>
                Ảnh
              </th>
              <th className={`border-r-2 border-slate-400 px-3 py-2 text-left w-36 ${isSticky ? 'sticky left-[400px] z-40 bg-slate-100 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.15)]' : ''}`}>
                Mã hàng
              </th>

              {/* Bán hàng Section */}
              <th className="border-r border-slate-200 px-2.5 py-2 text-left w-28 bg-slate-50 text-slate-600">Màu</th>
              <th className="border-r border-slate-200 px-3 py-2 text-left w-56 min-w-[180px] bg-slate-50 text-slate-600">Tên sản phẩm</th>
              <th className="border-r border-slate-200 px-2 py-2 text-right w-14 bg-slate-50 text-slate-600">W</th>
              <th className="border-r border-slate-200 px-2 py-2 text-right w-14 bg-slate-50 text-slate-600">D</th>
              <th className="border-r border-slate-200 px-2 py-2 text-right w-14 bg-slate-50 text-slate-600">H</th>
              <th className="border-r border-slate-200 px-2.5 py-2 text-center w-20 bg-slate-50 text-slate-600">Ráp/Tháo</th>
              <th className="border-r border-slate-200 px-2 py-2 text-center w-16 bg-slate-50 text-slate-600">ISTA</th>
              <th className="border-r border-slate-200 px-2.5 py-2 text-right w-20 bg-slate-50 text-slate-600 font-bold">SL đơn</th>
              <th className="border-r border-slate-200 px-2.5 py-2 text-left w-28 bg-slate-50 text-slate-600">PO khách</th>
              <th className="border-r-2 border-slate-300 px-2.5 py-2 text-left w-36 min-w-[120px] bg-slate-50 text-slate-600">Ghi chú Sale</th>

              {/* Kế hoạch Section */}
              <th className="border-r border-slate-200 px-2.5 py-2 text-right w-24 bg-sky-50 text-sky-950 font-bold">SL kế hoạch</th>
              <th className="border-r border-slate-200 px-2 py-2 text-left w-36 bg-sky-50 text-sky-950">Đơn vị mộc</th>
              <th className="border-r border-slate-200 px-2 py-2 text-left w-36 bg-sky-50 text-sky-950">Đơn vị sơn</th>
              <th className="border-r border-slate-200 px-2 py-2 text-left w-36 bg-sky-50 text-sky-950">Gia công ngoài</th>
              <th className="border-r border-slate-200 px-2 py-2 text-center w-28 bg-sky-50 text-sky-950">NVL về</th>
              <th className="border-r border-slate-200 px-2 py-2 text-center w-28 bg-sky-50 text-sky-950">Xong mộc</th>
              <th className="border-r border-slate-200 px-2 py-2 text-center w-28 bg-sky-50 text-sky-950">Xong sơn</th>
              <th className="border-r-2 border-sky-600 px-2.5 py-2 text-center w-36 bg-sky-200 text-sky-950 font-extrabold shadow-inner">
                HOÀN THÀNH (NHÀ MÁY)
              </th>
              <th className="border-r border-slate-200 px-2 py-2 text-center w-28 bg-sky-50 text-sky-950">Giao (ETD)</th>
              <th className="border-r border-slate-200 px-2 py-2 text-center w-24 bg-sky-50 text-sky-950">Đã xong</th>
              <th className="border-r-2 border-slate-300 px-2 py-2 text-center w-28 bg-sky-50 text-sky-950">Trạng thái</th>

              {/* Thao tác Section */}
              <th className="border-r border-slate-200 px-2 py-2 text-left w-44 min-w-[140px]">Ghi chú điều hành</th>
              <th className="px-2 py-2 text-center w-12">⋮</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200">
            {orders.length === 0 ? (
              <tr>
                <td colSpan={28} className="py-12 px-6 text-slate-400 text-base">
                  <div className="sticky left-0 w-fit max-w-[min(100%,48rem)]">
                    Không có lệnh sản xuất nào trong tháng này.
                  </div>
                </td>
              </tr>
            ) : (
              orders.map((order, idx) => {
                const isSelected = selectedId === order.id
                const isFlashing = flashingIds.includes(order.id)
                const isCompleted = order.done
                const isMismatch = Number(order.planned_qty) !== Number(order.quantity)

                // Validate milestone sequence
                const milestoneValidation = validateMilestoneOrder({
                  d_material_arrival: order.d_material_arrival,
                  d_white_done: order.d_white_done,
                  d_paint_done: order.d_paint_done,
                  d_factory_completion: order.d_factory_completion,
                  d_shipment: order.d_shipment,
                })

                // Status badge
                const statusBadge = getStatusBadgeInfo(
                  order.status,
                  order.days_late,
                  order.days_until_factory,
                )

                // Row background classes
                let rowBg = idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                if (isCompleted) {
                  rowBg = 'bg-emerald-50/80 border-l-4 border-l-emerald-600'
                }
                if (isFlashing) {
                  rowBg = 'bg-amber-100 ring-2 ring-amber-400 transition-colors duration-1000'
                }
                if (isSelected) {
                  rowBg = 'bg-sky-50/80 ring-1 ring-sky-300'
                }

                // Drag over classes
                let dragClass = ''
                if (dragOverId === order.id) {
                  dragClass = dragOverPos === 'top' ? 'border-t-2 border-t-emerald-600' : 'border-b-2 border-b-emerald-600'
                }

                const stickyBg = isCompleted
                  ? 'bg-emerald-50'
                  : isFlashing
                  ? 'bg-amber-100'
                  : isSelected
                  ? 'bg-sky-50'
                  : idx % 2 === 0
                  ? 'bg-white'
                  : 'bg-slate-50'

                // Dimension unit
                const dimUnit = order.dim_unit || 'inch'

                return (
                  <tr
                    key={order.id}
                    id={`plan-row-${order.id}`}
                    data-order-id={order.id}
                    draggable={isEditable}
                    onDragStart={(e) => handleDragStart(e, order.id)}
                    onDragOver={(e) => handleDragOver(e, order.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, order.id)}
                    onDragEnd={handleDragEnd}
                    onClick={() => handleRowClick(order.id)}
                    className={`transition-colors hover:bg-emerald-50/40 ${rowBg} ${dragClass}`}
                  >
                    {/* 1. STT & Drag Handle */}
                    <td
                      className={`border-r border-slate-200 px-1 py-1.5 text-center font-mono ${
                        isSticky ? `sticky left-0 z-10 ${stickyBg}` : ''
                      }`}
                    >
                      <div
                        className={`flex items-center justify-center gap-1 select-none ${
                          isEditable ? 'cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-800' : 'text-slate-400'
                        }`}
                        title={isEditable ? 'Kéo để đổi thứ tự (hoặc bấm chọn rồi dùng Alt+↑/↓)' : undefined}
                      >
                        <span className="text-xs font-bold leading-none">⋮⋮</span>
                        <span className="text-xs font-semibold">{idx + 1}</span>
                      </div>
                    </td>

                    {/* 2. Khách hàng */}
                    <td
                      className={`border-r border-slate-200 px-2 py-1.5 font-bold font-mono text-xs text-slate-800 truncate ${
                        isSticky ? `sticky left-12 z-10 ${stickyBg}` : ''
                      }`}
                      title={order.customer_name || order.customer_code || ''}
                    >
                      {order.customer_code || '—'}
                    </td>

                    {/* 3. Người mua */}
                    <td
                      className={`border-r border-slate-200 px-2 py-1.5 text-xs text-slate-700 truncate ${
                        isSticky ? `sticky left-40 z-10 ${stickyBg}` : ''
                      }`}
                      title={order.buyer_name || ''}
                    >
                      {order.buyer_name || '—'}
                    </td>

                    {/* 4. Ảnh */}
                    <td
                      className={`border-r border-slate-200 p-1 text-center ${
                        isSticky ? `sticky left-[336px] z-10 ${stickyBg}` : ''
                      }`}
                    >
                      <div
                        className="mx-auto flex h-11 w-11 cursor-pointer items-center justify-center rounded border border-slate-200 bg-slate-50 transition-transform hover:scale-105 hover:border-emerald-600"
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect()
                          setHoverCard({
                            visible: true,
                            order,
                            x: rect.right + 12,
                            y: rect.top - 20,
                          })
                        }}
                        onMouseLeave={() => setHoverCard({ visible: false, order: null, x: 0, y: 0 })}
                      >
                        <ProductThumbnail
                          productId={order.product_id}
                          hasImage={order.has_image}
                          alt={order.variant_code || 'Product'}
                          className="h-full w-full rounded object-cover"
                        />
                      </div>
                    </td>

                    {/* 5. Mã hàng + Source changed flag */}
                    <td
                      className={`border-r-2 border-slate-400 px-2.5 py-1.5 ${
                        isSticky
                          ? `sticky left-[400px] z-10 ${stickyBg} shadow-[4px_0_6px_-2px_rgba(0,0,0,0.15)]`
                          : ''
                      }`}
                    >
                      <div className="flex flex-col">
                        <span className="font-mono font-bold text-emerald-800 text-xs truncate">
                          {order.variant_code || '—'}
                        </span>
                        {/* Source Changed Banner */}
                        {order.source_changed && (
                          <div className="flex items-center gap-1 mt-0.5 px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-900 border border-amber-300 rounded leading-tight">
                            <span title={order.source_changed_note || 'Đơn đã đổi so với kế hoạch'}>
                              ⚠️ Đơn đã đổi
                            </span>
                            {isEditable && (
                              <button
                                type="button"
                                className="ml-auto px-1 py-0 text-xs font-bold bg-amber-200 hover:bg-amber-300 text-amber-950 rounded"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onAckChange(order.id)
                                }}
                              >
                                Đã xem
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* 6. Màu */}
                    <td className="border-r border-slate-200 px-2 py-1.5 bg-slate-50/60 text-xs">
                      <div className="flex items-center gap-1.5">
                        {order.swatch_hex && (
                          <span
                            className="inline-block h-4 w-4 rounded-full border border-slate-300 flex-shrink-0"
                            style={{ backgroundColor: order.swatch_hex }}
                          />
                        )}
                        <span className="truncate text-slate-700">{order.color_name || '—'}</span>
                      </div>
                    </td>

                    {/* 7. Tên sản phẩm */}
                    <td className="border-r border-slate-200 px-2.5 py-1.5 bg-slate-50/60 text-xs text-slate-800 truncate max-w-[200px]" title={order.product_name}>
                      {order.product_name || '—'}
                    </td>

                    {/* 8-10. W, D, H */}
                    <td className="border-r border-slate-200 px-2 py-1.5 bg-slate-50/60 text-right font-mono text-xs text-slate-700">
                      {formatDimPure(order.w, dimUnit)}
                    </td>
                    <td className="border-r border-slate-200 px-2 py-1.5 bg-slate-50/60 text-right font-mono text-xs text-slate-700">
                      {formatDimPure(order.d, dimUnit)}
                    </td>
                    <td className="border-r border-slate-200 px-2 py-1.5 bg-slate-50/60 text-right font-mono text-xs text-slate-700">
                      {formatDimPure(order.h, dimUnit)}
                    </td>

                    {/* 11. Ráp/Tháo */}
                    <td className="border-r border-slate-200 px-2 py-1.5 bg-slate-50/60 text-center text-xs text-slate-700">
                      {order.assembly_type === 'assembled' ? 'Ráp chết' : 'Tháo rời'}
                    </td>

                    {/* 12. ISTA */}
                    <td className="border-r border-slate-200 px-2 py-1.5 bg-slate-50/60 text-center font-mono text-xs text-slate-700">
                      {order.ista_level || '—'}
                    </td>

                    {/* 13. SL đơn */}
                    <td className="border-r border-slate-200 px-2.5 py-1.5 bg-slate-50/60 text-right font-mono text-xs font-bold text-slate-900">
                      {Number(order.quantity || 0).toLocaleString('vi-VN')}
                    </td>

                    {/* 14. PO khách */}
                    <td className="border-r border-slate-200 px-2 py-1.5 bg-slate-50/60 text-xs font-mono text-slate-700 truncate" title={order.customer_po || ''}>
                      {order.customer_po || '—'}
                    </td>

                    {/* 15. Ghi chú Sale */}
                    <td className="border-r-2 border-slate-300 px-2.5 py-1.5 bg-slate-50/60 text-xs text-slate-600 truncate max-w-[150px]" title={order.line_note || ''}>
                      {order.line_note || '—'}
                    </td>

                    {/* 16. SL kế hoạch */}
                    <td className="border-r border-slate-200 p-1 bg-sky-50/30">
                      {isEditable ? (
                        <input
                          type="number"
                          value={order.planned_qty ?? 0}
                          min={0}
                          onChange={(e) => {
                            const val = Math.max(0, parseInt(e.target.value, 10) || 0)
                            onUpdateOrder(order.id, { planned_qty: val })
                          }}
                          className={`w-full rounded border px-2 py-1 text-right font-mono text-xs font-bold outline-none transition-colors ${
                            isMismatch
                              ? 'border-amber-400 bg-amber-50 text-amber-900 focus:border-amber-600'
                              : 'border-slate-300 bg-white text-slate-900 focus:border-sky-500'
                          }`}
                          title={isMismatch ? `Khác SL đơn (${order.quantity})` : undefined}
                        />
                      ) : (
                        <span className={`block px-2 text-right font-mono text-xs font-bold ${isMismatch ? 'text-amber-700' : 'text-slate-900'}`}>
                          {Number(order.planned_qty || 0).toLocaleString('vi-VN')}
                        </span>
                      )}
                    </td>

                    {/* 17. Đơn vị mộc */}
                    <td className="border-r border-slate-200 p-1 bg-sky-50/30">
                      {isEditable ? (
                        <Typeahead<WorkUnit>
                          items={woodUnits}
                          value={woodUnits.find((u) => u.id === order.wood_unit_id) || null}
                          displayValue={(u) => u?.name || ''}
                          placeholder="Chọn đơn vị mộc..."
                          onChange={(u) => {
                            onUpdateOrder(order.id, {
                              wood_unit_id: u?.id || null,
                              wood_unit_code: u?.code || null,
                              wood_unit_name: u?.name || null,
                            })
                          }}
                          inputClassName="h-8 text-xs bg-white"
                        />
                      ) : (
                        <span className="block px-2 text-xs text-slate-700 truncate">
                          {order.wood_unit_name || '—'}
                        </span>
                      )}
                    </td>

                    {/* 18. Đơn vị sơn */}
                    <td className="border-r border-slate-200 p-1 bg-sky-50/30">
                      {isEditable ? (
                        <Typeahead<WorkUnit>
                          items={paintUnits}
                          value={paintUnits.find((u) => u.id === order.paint_unit_id) || null}
                          displayValue={(u) => u?.name || ''}
                          placeholder="Chọn đơn vị sơn..."
                          onChange={(u) => {
                            onUpdateOrder(order.id, {
                              paint_unit_id: u?.id || null,
                              paint_unit_code: u?.code || null,
                              paint_unit_name: u?.name || null,
                            })
                          }}
                          inputClassName="h-8 text-xs bg-white"
                        />
                      ) : (
                        <span className="block px-2 text-xs text-slate-700 truncate">
                          {order.paint_unit_name || '—'}
                        </span>
                      )}
                    </td>

                    {/* 19. Gia công ngoài */}
                    <td className="border-r border-slate-200 p-1 bg-sky-50/30">
                      {isEditable ? (
                        <Typeahead<WorkUnit>
                          items={subcontractUnits}
                          value={subcontractUnits.find((u) => u.id === order.subcontract_unit_id) || null}
                          displayValue={(u) => u?.name || ''}
                          placeholder="Gia công ngoài..."
                          onChange={(u) => {
                            onUpdateOrder(order.id, {
                              subcontract_unit_id: u?.id || null,
                              subcontract_unit_code: u?.code || null,
                              subcontract_unit_name: u?.name || null,
                            })
                          }}
                          inputClassName="h-8 text-xs bg-white"
                        />
                      ) : (
                        <span className="block px-2 text-xs text-slate-700 truncate">
                          {order.subcontract_unit_name || '—'}
                        </span>
                      )}
                    </td>

                    {/* 20. NVL về */}
                    <td className="border-r border-slate-200 p-1 bg-sky-50/30 text-center">
                      {isEditable ? (
                        <DateInput
                          value={order.d_material_arrival || ''}
                          onChange={(val) => onUpdateOrder(order.id, { d_material_arrival: val || null })}
                          className={`h-8 text-xs text-center font-mono ${
                            milestoneValidation.invalidFields.includes('d_material_arrival')
                              ? 'border-rose-500 bg-rose-50 text-rose-900'
                              : 'bg-white'
                          }`}
                        />
                      ) : (
                        <span className="font-mono text-xs text-slate-700">
                          {formatDate(order.d_material_arrival)}
                        </span>
                      )}
                    </td>

                    {/* 21. Xong mộc */}
                    <td className="border-r border-slate-200 p-1 bg-sky-50/30 text-center">
                      {isEditable ? (
                        <DateInput
                          value={order.d_white_done || ''}
                          onChange={(val) => onUpdateOrder(order.id, { d_white_done: val || null })}
                          className={`h-8 text-xs text-center font-mono ${
                            milestoneValidation.invalidFields.includes('d_white_done')
                              ? 'border-rose-500 bg-rose-50 text-rose-900'
                              : 'bg-white'
                          }`}
                        />
                      ) : (
                        <span className="font-mono text-xs text-slate-700">
                          {formatDate(order.d_white_done)}
                        </span>
                      )}
                    </td>

                    {/* 22. Xong sơn */}
                    <td className="border-r border-slate-200 p-1 bg-sky-50/30 text-center">
                      {isEditable ? (
                        <DateInput
                          value={order.d_paint_done || ''}
                          onChange={(val) => onUpdateOrder(order.id, { d_paint_done: val || null })}
                          className={`h-8 text-xs text-center font-mono ${
                            milestoneValidation.invalidFields.includes('d_paint_done')
                              ? 'border-rose-500 bg-rose-50 text-rose-900'
                              : 'bg-white'
                          }`}
                        />
                      ) : (
                        <span className="font-mono text-xs text-slate-700">
                          {formatDate(order.d_paint_done)}
                        </span>
                      )}
                    </td>

                    {/* 23. HOÀN THÀNH (NHÀ MÁY) */}
                    <td className="border-r-2 border-sky-600 p-1 bg-sky-100/50 text-center">
                      <div className="flex flex-col items-center">
                        {isEditable ? (
                          <DateInput
                            value={order.d_factory_completion || ''}
                            onChange={(val) => onUpdateOrder(order.id, { d_factory_completion: val || null })}
                            className={`h-8 text-xs text-center font-mono font-bold ${
                              milestoneValidation.invalidFields.includes('d_factory_completion')
                                ? 'border-rose-500 bg-rose-50 text-rose-900'
                                : 'border-sky-400 bg-white text-sky-950 font-bold'
                            }`}
                          />
                        ) : (
                          <span className="font-mono text-xs font-bold text-sky-950">
                            {formatDate(order.d_factory_completion)}
                          </span>
                        )}

                        {/* Baseline Delta Chip */}
                        {order.baseline?.d_factory_completion &&
                          order.d_factory_completion &&
                          order.baseline.d_factory_completion !== order.d_factory_completion && (
                            <span className="mt-0.5 inline-block text-xs font-medium bg-amber-200 text-amber-900 px-1 py-0.5 rounded leading-tight">
                              Gốc: {formatDate(order.baseline.d_factory_completion)}
                            </span>
                          )}

                        {/* Milestone order error tooltip text */}
                        {!milestoneValidation.valid && (
                          <span className="text-xs text-rose-600 font-semibold leading-none mt-0.5" title={milestoneValidation.message || undefined}>
                            ⚠️ Sai thứ tự
                          </span>
                        )}
                      </div>
                    </td>

                    {/* 24. Giao (ETD) */}
                    <td className="border-r border-slate-200 p-1 bg-sky-50/30 text-center">
                      {isEditable ? (
                        <DateInput
                          value={order.d_shipment || ''}
                          onChange={(val) => onUpdateOrder(order.id, { d_shipment: val || null })}
                          className={`h-8 text-xs text-center font-mono ${
                            milestoneValidation.invalidFields.includes('d_shipment')
                              ? 'border-rose-500 bg-rose-50 text-rose-900'
                              : 'bg-white'
                          }`}
                        />
                      ) : (
                        <span className="font-mono text-xs text-slate-700">
                          {formatDate(order.d_shipment)}
                        </span>
                      )}
                    </td>

                    {/* 25. Đã hoàn thành (checkbox) */}
                    <td className="border-r border-slate-200 p-1 bg-sky-50/30 text-center">
                      <input
                        type="checkbox"
                        checked={order.done}
                        disabled={!isEditable}
                        onChange={(e) => onUpdateOrder(order.id, { done: e.target.checked })}
                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer disabled:cursor-not-allowed"
                        title={order.done ? 'Đã hoàn thành' : 'Đang sản xuất'}
                      />
                    </td>

                    {/* 26. Trạng thái (status badge) */}
                    <td className="border-r-2 border-slate-300 p-1 bg-sky-50/30 text-center">
                      <span
                        className={`inline-block px-2 py-0.5 text-xs rounded-full border ${statusBadge.className}`}
                        title={statusBadge.title}
                      >
                        {statusBadge.label}
                      </span>
                    </td>

                    {/* 27. Ghi chú điều hành */}
                    <td className="border-r border-slate-200 p-1">
                      {isEditable ? (
                        <input
                          type="text"
                          value={order.note || ''}
                          placeholder="Ghi chú kế hoạch..."
                          onChange={(e) => onUpdateOrder(order.id, { note: e.target.value })}
                          className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-800 outline-none focus:border-sky-500"
                        />
                      ) : (
                        <span className="block px-2 text-xs text-slate-600 truncate max-w-[140px]">
                          {order.note || '—'}
                        </span>
                      )}
                    </td>

                    {/* 28. Thao tác ⋮ */}
                    <td className="p-1 text-center">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          const rect = e.currentTarget.getBoundingClientRect()
                          setMenuState({ order, anchorRect: rect })
                        }}
                        className="flex h-7 w-7 items-center justify-center mx-auto rounded text-slate-500 hover:bg-slate-200 hover:text-slate-800 text-base font-bold"
                        title="Thao tác dòng"
                      >
                        ⋮
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Floating 3-dots Row Menu */}
      {menuState.order && (
        <ProductionOrderRowMenu
          row={menuState.order}
          canSalesWrite={canWrite && !monthLocked}
          canDemandWrite={true}
          isOpen={Boolean(menuState.order)}
          anchorRect={menuState.anchorRect}
          onClose={() => setMenuState({ order: null, anchorRect: null })}
          onViewHistory={() => {
            if (menuState.order) onViewHistory(menuState.order)
            setMenuState({ order: null, anchorRect: null })
          }}
          onToggleUnit={() => {
            if (menuState.order) handleToggleDimUnit(menuState.order)
            setMenuState({ order: null, anchorRect: null })
          }}
          onReturnToDraft={() => {
            if (menuState.order) onReturnToDraft(menuState.order)
            setMenuState({ order: null, anchorRect: null })
          }}
          onPushToDemand={() => {
            if (menuState.order) onPushToDemand(menuState.order)
            setMenuState({ order: null, anchorRect: null })
          }}
          onOpenProductionDemand={() => {
            if (menuState.order) onOpenProductionDemand?.(menuState.order)
            setMenuState({ order: null, anchorRect: null })
          }}
          onOpenExecution={() => {
            if (menuState.order) onOpenExecution?.(menuState.order)
            setMenuState({ order: null, anchorRect: null })
          }}
        />
      )}

      {/* Hover Image Preview Card */}
      {hoverCard.visible && hoverCard.order && (
        <div
          className="fixed z-50 w-72 rounded-lg border border-slate-300 bg-white p-3 shadow-2xl pointer-events-none"
          style={{ left: hoverCard.x, top: hoverCard.y }}
        >
          <div className="flex h-56 w-full items-center justify-center overflow-hidden rounded bg-slate-100 border border-slate-200">
            <ProductThumbnail
              productId={hoverCard.order.product_id}
              hasImage={hoverCard.order.has_image}
              alt={hoverCard.order.variant_code || 'Preview'}
              className="h-full w-full object-contain"
            />
          </div>
          <div className="mt-2 text-xs">
            <div className="flex items-center justify-between font-mono font-bold text-slate-800">
              <span>{hoverCard.order.variant_code}</span>
              {hoverCard.order.color_name && (
                <span className="text-xs text-slate-500 font-normal">
                  {hoverCard.order.color_name}
                </span>
              )}
            </div>
            <p className="mt-0.5 font-medium text-slate-700 truncate">
              {hoverCard.order.product_name}
            </p>
            <p className="mt-1 font-mono text-slate-500 text-xs">
              W: {formatDimPure(hoverCard.order.w, hoverCard.order.dim_unit || 'inch')} × D:{' '}
              {formatDimPure(hoverCard.order.d, hoverCard.order.dim_unit || 'inch')} × H:{' '}
              {formatDimPure(hoverCard.order.h, hoverCard.order.dim_unit || 'inch')} (
              {hoverCard.order.dim_unit || 'inch'})
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
